import { observer } from "mobx-react-lite";
import { useCallback, useRef, useState, type ReactNode } from "react";
import { MoreVertical, SettingsIcon } from "lucide-react";

import { usePlayground } from "../../playground-store";
import { TEMPLATE_FORMATS } from "../../types";

import ModelSummaryChip from "./ModelSummaryChip";
import PromptObjectMenu from "./PromptObjectMenu";
import SaveTemplateDialog from "./SaveTemplateDialog";
import MessagesSettings from "./MessagesSettings";
import useXRayMode from "./MessagesSettings/hooks/useXRayMode";

import Banner from "@/components/ui/banner";
import FormatWarningBanner from "@/components/SchemaEditor/FormatWarningBanner";
import FormatChangeConfirmation from "@/components/SchemaEditor/FormatChangeConfirmation";
import SaveStatus from "@/components/ui/SaveStatus";
import EditTestCaseDialog from "@/components/Playground/Drawer/TestCases/TestCase/EditTestCaseDialog";
import { VariableSetsEmptyStateBanner } from "@/components/Playground/Messages/MessagesHeader/VariableSetSelector";
import { useOptionalPlaygroundTourContext } from "@/components/Playground/tour/context";
import { Badge, BadgeVariant } from "@/components/ui/Badge";
import { PromptBlueprint } from "@/types";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { useContainerWidth } from "@/hooks/useContainerWidth";

interface MessagesHeaderProps {
  title?: string;
  editorActions?: ReactNode;
}

const InlineMessagesHeader = observer(
  ({ title: titleProp }: Pick<MessagesHeaderProps, "title">) => {
    const playground = usePlayground();
    const { blueprint, isSnippet } = playground;
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const title = titleProp ?? "Inline prompt";

    const handleModeChange = useCallback(
      (isChat: boolean) => {
        playground.setMode(isChat ? "chat" : "completion");
      },
      [playground],
    );

    const handleApplyBlueprint = useCallback(
      (promptData: Partial<PromptBlueprint>) => {
        playground.applyPromptBlueprintToState(promptData, { preserveSessionToggles: true });
      },
      [playground],
    );

    return (
      <div className="relative flex w-full flex-col border-b border-gray-200 bg-white">
        <FormatChangeConfirmation isOpen={isConfirmModalOpen} setIsOpen={setIsConfirmModalOpen} />

        <div className="flex w-full flex-col gap-2 px-3 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex min-w-0 items-center gap-2">
            <div className="min-w-0">
              <div className="truncate text-sm font-medium text-gray-900">{title}</div>
            </div>
          </div>

          <div className="flex min-w-0 flex-wrap items-center gap-1.5 sm:justify-end">
            {!isSnippet && (
              <ModelSummaryChip
                isPromptChat={playground.isChat}
                onModeChange={handleModeChange}
                promptData={playground.blueprint}
                readonly={playground.readonly}
                onUpdate={handleApplyBlueprint}
                triggerClassName="h-8 max-w-full justify-start rounded-md px-2.5 text-gray-700 transition-colors hover:bg-gray-100 sm:max-w-[220px]"
              />
            )}

            <MessagesSettings
              readonly={playground.readonly}
              formats={TEMPLATE_FORMATS}
              currentFormat={playground.template_format}
              onFormatChange={playground.setTemplateFormat}
              tooltipContent="Inline prompt settings"
              dialogTitle="Inline prompt settings"
              dialogDescription="Adjust formatting and metadata for this inline prompt."
              metadata={playground.metadata}
              setMetadata={playground.setMetadata}
              enableMarkdown={playground.markdownEnabled}
              onToggleMarkdown={playground.toggleMarkdown}
              enableStreaming={playground.streamingEnabled}
              onToggleStreaming={playground.toggleStreaming}
              streamingRequiredByModel={playground.streamingRequiredByModel}
              streamingDisallowedByModel={playground.streamingDisallowedByModel}
              blueprint={blueprint ?? undefined}
              showStreaming={false}
              showMetadata={!isSnippet}
              showExport={false}
            />
          </div>
        </div>

        {playground.showJinjaWarning && (
          <div className="max-h-[120px] overflow-y-auto px-3 pb-3">
            <FormatWarningBanner
              setIsConfirmModalOpen={setIsConfirmModalOpen}
              description={
                <>
                  <span>Jinja2 detected. Please switch this inline prompt from </span>
                  <code className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm dark:bg-gray-800">
                    f-string
                  </code>
                  <span> to </span>
                  <code className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm dark:bg-gray-800">
                    jinja2
                  </code>
                  <span>. </span>
                </>
              }
            />
          </div>
        )}
      </div>
    );
  },
);

const MessagesHeader = ({ title: titleProp, editorActions }: MessagesHeaderProps) => {
  const playground = usePlayground();
  const tour = useOptionalPlaygroundTourContext();
  const { blueprint, isSavingSession, isSnippet } = playground;
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const { hasXRay, isXRayMode, toggleXRayMode, isLoading: isXRayLoading } = useXRayMode();
  const containerRef = useRef<HTMLDivElement>(null);
  const containerWidth = useContainerWidth(containerRef);

  // We collapse into the vertical dots menu if the container itself is less than 768px wide.
  // We use window width as a fallback heuristic for the initial render before resize observer fires.
  const isCompact = containerWidth === 0 ? window.innerWidth < 1024 : containerWidth < 768;

  const title = titleProp ?? (isSnippet ? undefined : "Playground");

  const snippetText = isSnippet
    ? (() => {
        const template = playground.blueprint?.prompt_template;
        if (template && "content" in template && template.content?.[0]?.type === "text") {
          return template.content[0].text;
        }
        return "";
      })()
    : undefined;

  const handleTourPopoverOpenChange = (open: boolean) => {
    if (!open) {
      return;
    }
    tour?.setModelPopoverOpen(true);
  };

  const handleModeChange = useCallback(
    (isChat: boolean) => {
      playground.setMode(isChat ? "chat" : "completion");
    },
    [playground],
  );

  const handleApplyBlueprint = useCallback(
    (promptData: Partial<PromptBlueprint>) => {
      playground.snippetOverrideStore.setSnippetOverrides({});
      playground.applyPromptBlueprintToState(promptData);
    },
    [playground],
  );

  const handleUpdateBlueprint = useCallback(
    (promptData: Partial<PromptBlueprint>) => {
      playground.applyPromptBlueprintToState(promptData, { preserveSessionToggles: true });
    },
    [playground],
  );

  if (playground.isInlineEditor) {
    return <InlineMessagesHeader title={titleProp} />;
  }

  return (
    <div className="relative flex w-full flex-col" ref={containerRef}>
      <FormatChangeConfirmation isOpen={isConfirmModalOpen} setIsOpen={setIsConfirmModalOpen} />

      <div className="flex min-h-12 w-full items-center justify-between gap-2 border-b border-gray-200 bg-white px-3 py-2 sm:gap-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2.5 overflow-hidden">
          <div className="flex min-w-0 items-center gap-2 overflow-hidden">
            <PromptObjectMenu
              title={title ?? (isSnippet ? "Snippet" : "Playground")}
              isSnippet={isSnippet}
              blueprint={blueprint}
              onApplyBlueprint={handleApplyBlueprint}
              snippetText={snippetText}
            />
            {playground.isEditor && playground.selectedTemplateVersion?.number !== undefined && (
              <Badge
                variant={BadgeVariant.BLUE}
                text={`Editing Version ${playground.selectedTemplateVersion.number}`}
              />
            )}
          </div>

          {!isCompact && (
            <SaveStatus
              isSaving={isSavingSession}
              updatedAt={playground.sessionUpdatedAt}
              className="ml-0.5 shrink-0"
            />
          )}
        </div>

        <div className="ml-auto flex shrink-0 items-center gap-1.5">
          {!isCompact ? (
            <>
              <div className="flex items-center gap-1.5">
                {!playground.isSnippet && (
                  <ModelSummaryChip
                    isPromptChat={playground.isChat}
                    onModeChange={handleModeChange}
                    promptData={playground.blueprint}
                    readonly={playground.readonly}
                    onUpdate={handleUpdateBlueprint}
                    controlledOpen={tour?.isActive ? tour.isModelPopoverOpen : undefined}
                    onControlledOpenChange={
                      tour?.isActive ? handleTourPopoverOpenChange : undefined
                    }
                  />
                )}
                {editorActions}
              </div>

              <div className="ml-1 flex items-center">
                <SaveTemplateDialog
                  disabled={playground.isAIPromptAssistantLoading}
                  size="default"
                />
              </div>

              <div className="block">
                <MessagesSettings
                  readonly={playground.readonly}
                  formats={TEMPLATE_FORMATS}
                  currentFormat={playground.template_format}
                  onFormatChange={playground.setTemplateFormat}
                  tooltipContent={isSnippet ? "Snippet Settings" : "Prompt Settings"}
                  metadata={playground.metadata}
                  setMetadata={playground.setMetadata}
                  enableMarkdown={playground.markdownEnabled}
                  onToggleMarkdown={playground.toggleMarkdown}
                  enableStreaming={playground.streamingEnabled}
                  onToggleStreaming={playground.toggleStreaming}
                  streamingRequiredByModel={playground.streamingRequiredByModel}
                  streamingDisallowedByModel={playground.streamingDisallowedByModel}
                  blueprint={blueprint ?? undefined}
                  isXRayMode={isXRayMode}
                  onToggleXRay={toggleXRayMode}
                  hasXRay={hasXRay}
                  isXRayLoading={isXRayLoading}
                  showStreaming={!isSnippet}
                  showMetadata={!isSnippet}
                  showExport={!isSnippet}
                  snippetText={snippetText}
                />
              </div>
            </>
          ) : (
            <>
              <div className="ml-1 flex items-center">
                <SaveTemplateDialog
                  disabled={playground.isAIPromptAssistantLoading}
                  size="default"
                />
              </div>

              <div className="block">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-500 hover:bg-gray-100 hover:text-gray-700"
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="end" className="flex w-56 flex-col gap-1 p-2">
                    {!playground.isSnippet && (
                      <ModelSummaryChip
                        isPromptChat={playground.isChat}
                        onModeChange={handleModeChange}
                        promptData={playground.blueprint}
                        readonly={playground.readonly}
                        onUpdate={handleUpdateBlueprint}
                        controlledOpen={tour?.isActive ? tour.isModelPopoverOpen : undefined}
                        onControlledOpenChange={
                          tour?.isActive ? handleTourPopoverOpenChange : undefined
                        }
                        triggerClassName="flex w-full h-8 items-center justify-start rounded-md px-2 text-sm text-gray-700 transition-colors hover:bg-gray-100"
                        functionDialogTriggerVariant="menu-item"
                      />
                    )}
                    {editorActions && (
                      <div className="flex flex-col gap-1 [&_[role=button]:hover]:!bg-gray-100 [&_[role=button]]:!flex [&_[role=button]]:!h-8 [&_[role=button]]:!w-full [&_[role=button]]:!items-center [&_[role=button]]:!justify-start [&_[role=button]]:!rounded-md [&_[role=button]]:!bg-transparent [&_[role=button]]:!px-2 [&_[role=button]]:!text-left [&_[role=button]]:!font-normal [&_[role=button]]:!text-gray-700 [&_a:hover]:!bg-gray-100 [&_a]:!flex [&_a]:!h-8 [&_a]:!w-full [&_a]:!items-center [&_a]:!justify-start [&_a]:!rounded-md [&_a]:!bg-transparent [&_a]:!px-2 [&_a]:!text-left [&_a]:!font-normal [&_a]:!text-gray-700 [&_button:hover]:!bg-gray-100 [&_button]:!flex [&_button]:!h-8 [&_button]:!w-full [&_button]:!items-center [&_button]:!justify-start [&_button]:!rounded-md [&_button]:!bg-transparent [&_button]:!px-2 [&_button]:!text-left [&_button]:!font-normal [&_button]:!text-gray-700">
                        {editorActions}
                      </div>
                    )}
                    <Button
                      variant="ghost"
                      className="flex h-8 w-full justify-start rounded-md px-2 font-normal text-gray-700 hover:bg-gray-100"
                      onClick={() => setMobileSettingsOpen(true)}
                    >
                      <SettingsIcon className="mr-2 h-4 w-4" />
                      Settings
                    </Button>
                  </PopoverContent>
                </Popover>

                <MessagesSettings
                  readonly={playground.readonly}
                  formats={TEMPLATE_FORMATS}
                  currentFormat={playground.template_format}
                  onFormatChange={playground.setTemplateFormat}
                  tooltipContent={isSnippet ? "Snippet Settings" : "Prompt Settings"}
                  metadata={playground.metadata}
                  setMetadata={playground.setMetadata}
                  enableMarkdown={playground.markdownEnabled}
                  onToggleMarkdown={playground.toggleMarkdown}
                  enableStreaming={playground.streamingEnabled}
                  onToggleStreaming={playground.toggleStreaming}
                  streamingRequiredByModel={playground.streamingRequiredByModel}
                  streamingDisallowedByModel={playground.streamingDisallowedByModel}
                  blueprint={blueprint ?? undefined}
                  isXRayMode={isXRayMode}
                  onToggleXRay={toggleXRayMode}
                  hasXRay={hasXRay}
                  isXRayLoading={isXRayLoading}
                  showStreaming={!isSnippet}
                  showMetadata={!isSnippet}
                  showExport={!isSnippet}
                  snippetText={snippetText}
                  open={mobileSettingsOpen}
                  onOpenChange={setMobileSettingsOpen}
                  hideTrigger
                />
              </div>
            </>
          )}
        </div>
      </div>

      <EditTestCaseDialog
        set={playground.input_variable_sets.find(set => set.id === playground.editingVariableSetId)}
        isOpen={playground.isEditingVariableSet}
        onClose={() => playground.stopEditingVariableSet()}
      />

      {playground.hasFormatMismatch && (
        <div className="max-h-[120px] overflow-y-auto">
          <div className="m-4">
            <Banner type="warning" prefix="Format mismatch:">
              Messages in this prompt have different template formats (set via API/SDK). The global
              format is shown as <strong>{playground.template_format}</strong>. Changing the format
              above will apply it to all messages and resolve this mismatch.
            </Banner>
          </div>
        </div>
      )}

      {playground.showJinjaWarning && (
        <div className="max-h-[120px] overflow-y-auto">
          <div className="m-4">
            <FormatWarningBanner
              setIsConfirmModalOpen={setIsConfirmModalOpen}
              description={
                <>
                  <span>Jinja2 detected. Please switch your prompt template from </span>
                  <code className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm dark:bg-gray-800">
                    f-string
                  </code>
                  <span> to </span>
                  <code className="whitespace-nowrap rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm dark:bg-gray-800">
                    jinja2
                  </code>
                  <span>. </span>
                </>
              }
            />
          </div>
        </div>
      )}

      <VariableSetsEmptyStateBanner />
    </div>
  );
};

export default observer(MessagesHeader);
